import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { transfersApi, reportsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  Loader2, 
  Send, 
  Home, 
  History, 
  Search, 
  ChevronRight,
  Download,
  Building2,
  Zap,
  Globe,
  Wallet,
  BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function TransactionsPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const serviceIcons = {
    swift: <Building2 className="w-5 h-5" />,
    moneygram: <Zap className="w-5 h-5" />,
    western_union: <Globe className="w-5 h-5" />,
    wire_transfer: <Send className="w-5 h-5" />,
    ach: <Wallet className="w-5 h-5" />,
    sepa: <BadgeCheck className="w-5 h-5" />,
    instant: <Zap className="w-5 h-5" />
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await transfersApi.getHistory();
      setTransactions(res.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await reportsApi.downloadPdf();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MoneyMove_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0, rotate: -180 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center"
        >
          <motion.div
            animate={{ 
              boxShadow: [
                '0 0 20px rgba(0, 255, 136, 0.3)',
                '0 0 40px rgba(0, 255, 136, 0.6)',
                '0 0 20px rgba(0, 255, 136, 0.3)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-20 h-20 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-4"
          >
            <Send className="w-10 h-10 text-black" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Loader2 className="w-8 h-8 text-[#00ff88] animate-spin mx-auto" />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="transactions-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#111] border-r border-white/5 p-6 hidden lg:flex flex-col">
        <Link to="/dashboard" className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
            <Send className="w-5 h-5 text-black" />
          </div>
          <span className="text-xl font-bold text-white">MoneyMove</span>
        </Link>

        <nav className="flex-1 space-y-2">
          <Link to="/dashboard" className="sidebar-link">
            <Home className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/send" className="sidebar-link">
            <Send className="w-5 h-5" />
            <span>Send Money</span>
          </Link>
          <Link to="/transactions" className="sidebar-link active">
            <History className="w-5 h-5" />
            <span>Transactions</span>
          </Link>
          <Link to="/track" className="sidebar-link">
            <Search className="w-5 h-5" />
            <span>Track Transfer</span>
          </Link>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8 pb-24 lg:pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                <ChevronRight className="w-5 h-5 rotate-180" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white">Transactions</h1>
              <p className="text-gray-400">View all your transfer history</p>
            </div>
          </div>
          
          <Button 
            onClick={handleDownloadReport}
            disabled={downloadingReport}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="download-report-btn"
          >
            {downloadingReport ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>

        {/* Transactions List */}
        <div className="card-dark">
          {transactions.length > 0 ? (
            <div className="divide-y divide-white/5">
              {transactions.map((tx) => (
                <Link 
                  key={tx.id} 
                  to={`/track?number=${tx.tracking_number}`}
                  className="p-5 flex items-center justify-between hover:bg-white/2 block"
                  data-testid={`transaction-${tx.tracking_number}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00ff88]/10 flex items-center justify-center text-[#00ff88]">
                      {serviceIcons[tx.service_type] || <Send className="w-6 h-6" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{tx.recipient_name}</p>
                      <p className="text-gray-500 text-sm">{tx.service_name} • {tx.recipient_country}</p>
                      <p className="text-gray-600 text-xs font-mono mt-1">{tx.tracking_number}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold amount-display text-lg">
                      -${tx.amount.toFixed(2)}
                    </p>
                    <span className={tx.status === 'completed' ? 'status-completed' : 'status-processing'}>
                      {tx.status}
                    </span>
                    <p className="text-gray-600 text-xs mt-1">{formatDate(tx.created_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-16 text-center">
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <History className="w-10 h-10 text-gray-600" />
              </div>
              <p className="text-gray-400 text-lg">No transactions yet</p>
              <p className="text-gray-600 text-sm mt-2">Your transfer history will appear here</p>
              <Link to="/send">
                <Button className="mt-6 bg-[#00ff88] text-black font-bold">
                  Send Your First Transfer
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#111] border-t border-white/5 p-4">
        <div className="flex justify-around">
          <Link to="/dashboard" className="flex flex-col items-center text-gray-500">
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/send" className="flex flex-col items-center text-gray-500">
            <Send className="w-6 h-6" />
            <span className="text-xs mt-1">Send</span>
          </Link>
          <Link to="/transactions" className="flex flex-col items-center text-[#00ff88]">
            <History className="w-6 h-6" />
            <span className="text-xs mt-1">History</span>
          </Link>
          <Link to="/track" className="flex flex-col items-center text-gray-500">
            <Search className="w-6 h-6" />
            <span className="text-xs mt-1">Track</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
